import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { faColumns3, faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from 'antd'
import { HStack, Input, VStack } from 'govwell-ui'
import { computed, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  CandidateCustomColumnFragment,
  ViewColumnTemplateFragment,
  ViewColumnType,
} from 'types/graphql'

import { Drawer } from 'src/components/Drawer'
import CandidateSectionHeader, {
  CANDIDATE_SECTION_KEY_PREFIX,
} from 'src/components/TableViews/ManageColumnsDrawer/CandidateSectionHeader'
import DrawerDragOverlay from 'src/components/TableViews/ManageColumnsDrawer/DrawerDragOverlay'
import HiddenColumnsHeader, {
  HIDDEN_SECTION_HEADER_KEY,
} from 'src/components/TableViews/ManageColumnsDrawer/HiddenColumnsHeader'
import ManageColumnsDrawerItem from 'src/components/TableViews/ManageColumnsDrawer/ManageColumnsDrawerItem'
import {
  EmptySearchHint,
  StyledHeader,
} from 'src/components/TableViews/ManageColumnsDrawer/styled-components'
import UpdateCustomColumnModal from 'src/components/TableViews/ManageColumnsDrawer/UpdateCustomColumnModal'
import {
  filterColumnTemplates,
  matchesSearch,
  shouldShowSection,
  useFilteredDrawerState,
  ColumnSection,
} from 'src/components/TableViews/ManageColumnsDrawer/utils'
import { useTableViewConfig } from 'src/components/TableViews/TableViewConfigContext'
import {
  useCreateViewCustomColumnTemplateMutation,
  useGetCandidateCustomColumnsQuery,
} from 'src/fetch/views/viewColumnTemplates'
import useDisclosure, { UseDisclosureReturn } from 'src/hooks/use-disclosure'
import { TableViewColumn } from 'src/models/TableViews/TableViewColumn'
import { groupBy } from 'src/utils/collections'
import { QueryKey } from 'src/utils/queryClient'

const generateCandidateColumnId = (candidate: CandidateCustomColumnFragment): string =>
  `candidate:${candidate.columnType}:${candidate.sourceId}`

const mapCandidateToColumnTemplate = (
  candidate: CandidateCustomColumnFragment
): ViewColumnTemplateFragment => ({
  __typename: 'ViewColumnTemplate',
  columnType: candidate.columnType,
  dataType: 'String',
  generatedId: generateCandidateColumnId(candidate),
  labelSentenceCase: candidate.label,
  labelTitleCase: candidate.label,
  fieldTemplates: [],
  filterOptions: [],
  includeByDefault: false,
  isFilterable: false,
  isSortable: false,
  isNullable: false,
  usedInViews: [],
})

type Props = {
  modalState: UseDisclosureReturn
}
const ManageColumnsDrawer = ({ modalState }: Props) => {
  const {
    columnManager,
    columnTemplateStore,
    customizableColumnTemplatesByType,
    hasCustomizableColumnTemplates,
    onSave,
    recordTypeId,
    viewDashboardId,
    viewType,
  } = useTableViewConfig()
  const queryClient = useQueryClient()
  const { mutateAsync: createCustomColumnTemplate } = useCreateViewCustomColumnTemplateMutation()
  const createCustomColumnModalState = useDisclosure()

  const [searchQuery, setSearchQuery] = useState('')
  const [collapsedSectionKeys, setCollapsedSectionKeys] = useState(new Set<string>())

  // Reset search and collapse state when drawer opens
  useEffect(() => {
    if (modalState.isOpen) {
      setSearchQuery('')
      setCollapsedSectionKeys(new Set())
    }
  }, [modalState.isOpen])

  const isSearching = searchQuery.length > 0

  // The hook's `enabled` guard already short-circuits when `viewType` is
  // missing, but its input type doesn't accept `null` for the optional
  // scoping fields — coerce to `undefined` to keep the type narrow while
  // preserving the no-op behavior for the not-yet-selected report case.
  const { data: candidateColumnsData } = useGetCandidateCustomColumnsQuery({
    viewType: viewType ?? undefined,
    viewDashboardId: viewDashboardId ?? undefined,
    recordTypeId: recordTypeId ?? undefined,
  })
  const candidateColumns = candidateColumnsData?.getCandidateCustomColumns?.candidates

  // Tracks candidates that have been created as real columns this session.
  // Lets us hide the candidate drawer item immediately (before the query refetches).
  // Reset when candidateColumns changes (i.e. query refetched with fresh server data).
  const [consumedCandidateIds, setConsumedCandidateIds] = useState(new Set<string>())
  useEffect(() => {
    setConsumedCandidateIds(new Set())
  }, [candidateColumns])

  const [loadingColumnIds, setLoadingColumnIds] = useState(new Set<string>())
  const inFlightCreationsRef = useRef(0)
  const saveInFlightRef = useRef(false)
  const needsResaveRef = useRef(false)

  const candidateTemplatesBySection = useMemo(() => {
    const available = (candidateColumns ?? []).filter(
      (c) => !consumedCandidateIds.has(generateCandidateColumnId(c))
    )
    const grouped = groupBy(available, (c) => c.sectionLabel)
    const map = new Map<string, ViewColumnTemplateFragment[]>()
    for (const [sectionLabel, candidates] of Object.entries(grouped)) {
      map.set(sectionLabel, candidates?.map(mapCandidateToColumnTemplate) ?? [])
    }
    return map
  }, [candidateColumns, consumedCandidateIds])

  const candidateLookup = useMemo(() => {
    const map = new Map<string, CandidateCustomColumnFragment>()
    for (const candidate of candidateColumns ?? []) {
      const id = generateCandidateColumnId(candidate)
      if (!consumedCandidateIds.has(id)) {
        map.set(id, candidate)
      }
    }
    return map
  }, [candidateColumns, consumedCandidateIds])

  const sectionLabelByColumnType = useMemo(() => {
    const map = new Map<ViewColumnType, string>()
    for (const info of candidateColumnsData?.getCandidateCustomColumns
      ?.candidateDiscoverableColumnTypeInfo ?? []) {
      map.set(info.columnType, info.sectionLabel)
    }
    return map
  }, [candidateColumnsData?.getCandidateCustomColumns?.candidateDiscoverableColumnTypeInfo])

  const candidateColumnTypes = useMemo(
    () => new Set(sectionLabelByColumnType.keys()),
    [sectionLabelByColumnType]
  )

  const isEditableCustomColumn = useCallback(
    (columnType: ViewColumnType) =>
      customizableColumnTemplatesByType.has(columnType) && !candidateColumnTypes.has(columnType),
    [customizableColumnTemplatesByType, candidateColumnTypes]
  )

  // Coalesces concurrent saves: if a save is in flight when another
  // edit lands, mark `needsResaveRef` and chain a follow-up call when
  // the in-flight one resolves. Saved-table-view persistence runs
  // through `onSave` (updateView mutation); the report surface's
  // `onSave` is a no-op so this collapses to a cheap noop chain.
  const flushSave = useCallback(async () => {
    if (saveInFlightRef.current) {
      needsResaveRef.current = true
      return
    }
    saveInFlightRef.current = true
    try {
      await onSave()
    } finally {
      saveInFlightRef.current = false
      if (needsResaveRef.current) {
        needsResaveRef.current = false
        await flushSave()
      }
    }
  }, [onSave])

  const createAndRegisterCandidate = useCallback(
    async (
      columnGeneratedId: string,
      afterAddTemplate?: (generatedId: string) => void
    ): Promise<string> => {
      if (!columnManager) {
        return ''
      }
      const candidate = candidateLookup.get(columnGeneratedId)
      if (!candidate) {
        return ''
      }
      setLoadingColumnIds((prev) => new Set([...prev, columnGeneratedId]))
      inFlightCreationsRef.current++
      try {
        const result = await runInAction(async () => {
          return await createCustomColumnTemplate({
            input: {
              columnType: candidate.columnType,
              configuredLabel: candidate.label,
              fieldTemplateIds: [candidate.sourceId],
              recordTypeId,
              viewDashboardId,
            },
          })
        })
        const newGeneratedId = result.createViewCustomColumnTemplate.generatedId
        runInAction(() => {
          columnTemplateStore.addColumnTemplate(result.createViewCustomColumnTemplate)
          afterAddTemplate?.(newGeneratedId)
        })
        setConsumedCandidateIds((prev) => new Set([...prev, columnGeneratedId]))
        return newGeneratedId
      } catch {
        return ''
      } finally {
        setLoadingColumnIds((prev) => {
          const next = new Set(prev)
          next.delete(columnGeneratedId)
          return next
        })
        inFlightCreationsRef.current--
        if (inFlightCreationsRef.current === 0) {
          await flushSave()
        }
      }
    },
    [
      candidateLookup,
      columnManager,
      columnTemplateStore,
      createCustomColumnTemplate,
      flushSave,
      recordTypeId,
      viewDashboardId,
    ]
  )

  const handleColumnVisibilityToggled = useCallback(
    async (columnGeneratedId: string) => {
      if (!columnManager) {
        return
      }
      if (candidateLookup.has(columnGeneratedId)) {
        await createAndRegisterCandidate(columnGeneratedId, (genId) => {
          columnManager.toggleColumnTypeVisibility(genId)
        })
        return
      }
      const existingTemplate = columnTemplateStore.columnTemplates.find(
        (ct) => ct.generatedId === columnGeneratedId
      )
      if (!existingTemplate) {
        return
      }
      runInAction(() => {
        columnManager.toggleColumnTypeVisibility(columnGeneratedId)
      })
      await flushSave()
    },
    [candidateLookup, columnManager, columnTemplateStore, createAndRegisterCandidate, flushSave]
  )

  const handleColumnsReordered = useCallback(
    async (e: DragEndEvent) => {
      if (!columnManager) {
        return
      }
      const draggedColumnId = e.active?.id as string
      if (!e.over) {
        return
      }
      const displacedColumnId = e.over?.id as string

      // --- Candidate-to-shown drag (optimistic) ---
      // Candidates don't have a real column template yet — one must be created via API.
      // To avoid a visual snap-back (dnd-kit resets transforms on drag end), we use a
      // two-phase optimistic approach: insert a fake template + column synchronously so
      // the item appears in "Shown" on the very first post-drag render, then swap in the
      // real IDs once the API responds. On error, we roll back.
      const candidate = candidateLookup.get(draggedColumnId)
      if (candidate) {
        const destinationIndex = columnManager.columns.findIndex(
          (c) => c.columnTemplateGeneratedId === displacedColumnId
        )
        const isDestinationShown =
          displacedColumnId === HIDDEN_SECTION_HEADER_KEY || destinationIndex >= 0
        if (!isDestinationShown) {
          return
        }

        // Phase 1: Synchronous optimistic update — must complete before any await so
        // React 18 batches the MobX + state updates into a single render.
        const fakeTemplate = mapCandidateToColumnTemplate(candidate)
        runInAction(() => {
          columnTemplateStore.addColumnTemplate(fakeTemplate)
          const newColumn = new TableViewColumn({
            columnGeneratedId: draggedColumnId,
            columnTemplateStore,
            displayIndex: destinationIndex >= 0 ? destinationIndex : columnManager.columns.length,
          })
          const newColumns = [...columnManager.columns]
          if (displacedColumnId === HIDDEN_SECTION_HEADER_KEY) {
            newColumns.push(newColumn)
          } else {
            newColumns.splice(destinationIndex, 0, newColumn)
          }
          columnManager.columns = newColumns
        })
        setConsumedCandidateIds((prev) => new Set([...prev, draggedColumnId]))

        // Phase 2: Create the real column template via API, then swap fake IDs for real ones.
        try {
          const result = await createCustomColumnTemplate({
            input: {
              columnType: candidate.columnType,
              configuredLabel: candidate.label,
              fieldTemplateIds: [candidate.sourceId],
              recordTypeId,
              viewDashboardId,
            },
          })
          const realTemplate = result.createViewCustomColumnTemplate
          // Add real template BEFORE deleting fake — columnManager.columns is a getter
          // that filters by !!columnTemplate, so deleting the fake first would make the
          // temp column invisible to findIndex and silently skip the swap.
          runInAction(() => {
            columnTemplateStore.addColumnTemplate(realTemplate)
            const tempIndex = columnManager.columns.findIndex(
              (c) => c.columnTemplateGeneratedId === draggedColumnId
            )
            if (tempIndex >= 0) {
              const newColumns = [...columnManager.columns]
              newColumns.splice(
                tempIndex,
                1,
                new TableViewColumn({
                  columnGeneratedId: realTemplate.generatedId,
                  columnTemplateStore,
                  displayIndex: tempIndex,
                })
              )
              columnManager.columns = newColumns
            }
            columnTemplateStore.deleteColumnTemplate(fakeTemplate)
          })
          await queryClient.invalidateQueries({ queryKey: [QueryKey.CandidateCustomColumns] })
          await onSave()
        } catch {
          // Phase 3: Roll back — remove fake template + temp column, un-consume candidate.
          // No explicit toast needed; useMutation's global onError handler shows the notification.
          runInAction(() => {
            columnTemplateStore.deleteColumnTemplate(fakeTemplate)
            columnManager.columns = columnManager.columns.filter(
              (c) => c.columnTemplateGeneratedId !== draggedColumnId
            )
          })
          setConsumedCandidateIds((prev) => {
            const next = new Set(prev)
            next.delete(draggedColumnId)
            return next
          })
        }
        return
      }

      // --- Normal reorder / show / hide ---
      // This entire block must be synchronous so dnd-kit's transform reset doesn't
      // cause a visual snap-back before the state update fires.
      // Candidates are already handled above, so any item here is an existing template.
      const sourceIndex = columnManager.columns.findIndex(
        (c) => c.columnTemplateGeneratedId === draggedColumnId
      )
      const destinationIndex = columnManager.columns.findIndex(
        (c) => c.columnTemplateGeneratedId === displacedColumnId
      )
      const newColumns = [...columnManager.columns]

      if (sourceIndex < 0) {
        const isDestinationShown =
          displacedColumnId === HIDDEN_SECTION_HEADER_KEY || destinationIndex >= 0
        if (!isDestinationShown) {
          return
        }
        const existingTemplate = columnTemplateStore.columnTemplates.find(
          (ct) => ct.generatedId === draggedColumnId
        )
        if (!existingTemplate) {
          return
        }
        const newColumn = new TableViewColumn({
          columnGeneratedId: draggedColumnId,
          columnTemplateStore,
          displayIndex: destinationIndex,
        })
        if (displacedColumnId === HIDDEN_SECTION_HEADER_KEY) {
          newColumns.push(newColumn)
        } else if (destinationIndex >= 0) {
          newColumns.splice(destinationIndex, 0, newColumn)
        }
      } else if (destinationIndex < 0) {
        if (columnManager.columns.length <= 1) {
          return
        }
        newColumns.splice(sourceIndex, 1)
      } else {
        const sourceColumn = newColumns.splice(sourceIndex, 1)[0]
        if (sourceColumn) {
          newColumns.splice(destinationIndex, 0, sourceColumn)
        }
      }

      runInAction(() => {
        columnManager.columns = newColumns
      })
      await onSave()
    },
    [
      candidateLookup,
      columnManager,
      columnTemplateStore,
      createCustomColumnTemplate,
      onSave,
      queryClient,
      recordTypeId,
      viewDashboardId,
    ]
  )

  const handleToggleSection = useCallback((sectionKey: string) => {
    setCollapsedSectionKeys((prev) => {
      const next = new Set(prev)
      if (next.has(sectionKey)) {
        next.delete(sectionKey)
      } else {
        next.add(sectionKey)
      }
      return next
    })
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 1 },
    })
  )
  const verticalModifiers = useMemo(() => [restrictToVerticalAxis], [])

  const hiddenColumnTemplates = computed(() => {
    if (!columnManager) {
      return []
    }
    return columnTemplateStore.columnTemplates
      .filter(
        (ct) =>
          !columnManager.columnsByColumnGeneratedId.has(ct.generatedId) &&
          !sectionLabelByColumnType.has(ct.columnType)
      )
      .sort((a, b) => a.labelSentenceCase.localeCompare(b.labelSentenceCase))
  }).get()

  const allCandidateTemplatesBySection = computed(() => {
    const map = new Map<string, ViewColumnTemplateFragment[]>()

    for (const [sectionLabel, templates] of candidateTemplatesBySection) {
      map.set(sectionLabel, [...templates])
    }

    if (columnManager) {
      for (const ct of columnTemplateStore.columnTemplates) {
        if (columnManager.columnsByColumnGeneratedId.has(ct.generatedId)) {
          continue
        }
        const sectionLabel = sectionLabelByColumnType.get(ct.columnType)
        if (!sectionLabel) {
          continue
        }
        const existing = map.get(sectionLabel) ?? []
        existing.push(ct)
        map.set(sectionLabel, existing)
      }
    }

    for (const templates of map.values()) {
      templates.sort((a, b) => a.labelSentenceCase.localeCompare(b.labelSentenceCase))
    }

    return map
  }).get()

  // Filtered shown columns (preserving TableViewColumn ordering)
  const shownColumns = columnManager?.columns ?? []
  const filteredShownColumns = useMemo(() => {
    if (!isSearching) {
      return shownColumns
    }
    return shownColumns.filter(
      (c) => c.columnTemplate && matchesSearch(c.columnTemplate.labelSentenceCase, searchQuery)
    )
  }, [shownColumns, isSearching, searchQuery])

  // Filtered hidden column templates
  const filteredHiddenTemplates = useMemo(
    () => filterColumnTemplates(hiddenColumnTemplates, searchQuery),
    [hiddenColumnTemplates, searchQuery]
  )

  // Filtered candidate sections with collapse support
  const candidateSections = useMemo((): ColumnSection[] => {
    return [...allCandidateTemplatesBySection.entries()].map(([sectionLabel, templates]) => ({
      key: `${CANDIDATE_SECTION_KEY_PREFIX}${sectionLabel}`,
      label: sectionLabel,
      isCollapsible: true,
      templates,
    }))
  }, [allCandidateTemplatesBySection])

  const filteredCandidateState = useFilteredDrawerState({
    sections: candidateSections,
    searchQuery,
    collapsedSectionKeys,
  })

  const hasAnyMatch =
    filteredShownColumns.length > 0 ||
    filteredHiddenTemplates.length > 0 ||
    filteredCandidateState.hasAnyMatch

  const keys = computed(() => [
    ...shownColumns.map((c) => c.columnTemplateGeneratedId),
    HIDDEN_SECTION_HEADER_KEY,
    ...hiddenColumnTemplates.map((ct) => ct.generatedId),
    ...[...allCandidateTemplatesBySection.entries()].flatMap(([sectionLabel, templates]) => [
      `${CANDIDATE_SECTION_KEY_PREFIX}${sectionLabel}`,
      ...templates.map((ct) => ct.generatedId),
    ]),
  ]).get()

  const shownColumnIds = useMemo(
    () => new Set(shownColumns.map((c) => c.columnTemplateGeneratedId)),
    [shownColumns]
  )

  // Flat list of all templates (shown + hidden + candidate) so the drag overlay can look up the active item's label
  const allTemplates = useMemo(() => {
    const templates: ViewColumnTemplateFragment[] = []
    for (const col of shownColumns) {
      if (col.columnTemplate) {
        templates.push(col.columnTemplate)
      }
    }
    templates.push(...hiddenColumnTemplates)
    for (const sectionTemplates of allCandidateTemplatesBySection.values()) {
      templates.push(...sectionTemplates)
    }
    return templates
  }, [shownColumns, hiddenColumnTemplates, allCandidateTemplatesBySection])

  return (
    <Drawer {...modalState} title="Manage Columns" icon={faColumns3} width="400px">
      <VStack gap={3} width="100%" align="stretch">
        <HStack gap={2} width="100%" align="stretch">
          <Input
            ariaLabel="Search columns"
            prefixIcon={faSearch}
            isClearable
            placeholder="Search columns..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          {hasCustomizableColumnTemplates && (
            <Button
              onClick={createCustomColumnModalState.open}
              icon={<FontAwesomeIcon icon={faPlus} />}
              className="h-auto whitespace-nowrap"
            >
              Create column
            </Button>
          )}
        </HStack>
        {createCustomColumnModalState.isOpen && (
          <UpdateCustomColumnModal
            modalState={createCustomColumnModalState}
            mode="create"
            onColumnVisibilityToggled={handleColumnVisibilityToggled}
          />
        )}
        <VStack width="100%" align="stretch">
          <DndContext
            collisionDetection={closestCenter}
            sensors={sensors}
            modifiers={verticalModifiers}
            onDragEnd={handleColumnsReordered}
          >
            <SortableContext items={keys} strategy={verticalListSortingStrategy}>
              {isSearching && !hasAnyMatch ? (
                <EmptySearchHint>No matching columns</EmptySearchHint>
              ) : (
                <>
                  <StyledHeader>Shown in table</StyledHeader>
                  {filteredShownColumns.map((column) => {
                    if (!column.columnTemplate) {
                      return null
                    }
                    return (
                      <ManageColumnsDrawerItem
                        columnTemplate={column.columnTemplate}
                        isEditable={isEditableCustomColumn(column.columnTemplate.columnType)}
                        isLoading={false}
                        isVisible
                        onColumnVisibilityToggled={handleColumnVisibilityToggled}
                        key={column.columnTemplateGeneratedId}
                      />
                    )
                  })}
                  {isSearching && filteredShownColumns.length === 0 && (
                    <EmptySearchHint>No matching columns</EmptySearchHint>
                  )}
                  <HiddenColumnsHeader
                    showEmptyHint={isSearching && filteredHiddenTemplates.length === 0}
                  />
                  {filteredHiddenTemplates.map((ct) => (
                    <ManageColumnsDrawerItem
                      columnTemplate={ct}
                      isEditable={isEditableCustomColumn(ct.columnType)}
                      isLoading={false}
                      isVisible={false}
                      onColumnVisibilityToggled={handleColumnVisibilityToggled}
                      key={ct.generatedId}
                    />
                  ))}
                  {filteredCandidateState.sections
                    .filter((section) => shouldShowSection(section, isSearching))
                    .map((section) => (
                      <Fragment key={section.key}>
                        <CandidateSectionHeader
                          count={section.filteredTemplates.length}
                          id={section.key}
                          isExpanded={section.isExpanded}
                          isSearching={isSearching}
                          label={section.label}
                          onToggle={handleToggleSection}
                          showEmptyHint={isSearching && section.isExpanded && section.isEmpty}
                        />
                        {section.isExpanded &&
                          section.filteredTemplates.map((ct) => (
                            <ManageColumnsDrawerItem
                              columnTemplate={ct}
                              isEditable={false}
                              isLoading={loadingColumnIds.has(ct.generatedId)}
                              isVisible={false}
                              onColumnVisibilityToggled={handleColumnVisibilityToggled}
                              key={ct.generatedId}
                            />
                          ))}
                      </Fragment>
                    ))}
                </>
              )}
            </SortableContext>
            <DrawerDragOverlay shownColumnIds={shownColumnIds} allTemplates={allTemplates} />
          </DndContext>
        </VStack>
      </VStack>
    </Drawer>
  )
}

export default observer(ManageColumnsDrawer)
